import { BigNumber, ethers } from 'ethers';
import { Logger } from 'ethers/lib/utils';
import { generateContractIdOfErc20Htlc } from '../utils/htlc';
import { HtlcServiceInterface, NewContractType, TxReturnType } from './type';

export function uint8ArrayToHexString(uint8: Uint8Array) {
  return Buffer.from(uint8).toString(`hex`);
}

export default class Erc20HtlcService implements HtlcServiceInterface {
  private erc20: ethers.Contract;

  private htlc: ethers.Contract;

  private signer: ethers.Signer;

  private signerAddress: string;

  constructor(
    erc20Address: string,
    htlcAddress: string,
    erc20Abi: any,
    htlcAbi: any,
    signer: ethers.Signer,
    signerAddress: string,
  ) {
    this.signer = signer;
    this.erc20 = new ethers.Contract(erc20Address, erc20Abi, signer);
    this.htlc = new ethers.Contract(htlcAddress, htlcAbi, signer);
    this.signerAddress = signerAddress;
  }

  static async build(
    erc20Address: string,
    htlcAddress: string,
    erc20Abi: any,
    htlcAbi: any,
    signer: ethers.Signer,
  ): Promise<Erc20HtlcService> {
    const signerAddress = await signer.getAddress();
    return new Erc20HtlcService(
      erc20Address,
      htlcAddress,
      erc20Abi,
      htlcAbi,
      signer,
      signerAddress,
    );
  }

  async approveErc20(
    amount: BigNumber,
    isReset: boolean,
  ): Promise<TxReturnType> {
    let tx;
    let receipt;
    try {
      const erc20Allowance = await this.erc20.allowance(
        this.signerAddress,
        this.htlc.address,
      );

      const isAllowance =
        ethers.BigNumber.from(erc20Allowance).toString() !== `0`;

      if (ethers.BigNumber.from(erc20Allowance).lt(amount)) {
        if (isAllowance && isReset) {
          tx = await this.erc20.approve(this.htlc.address, 0);
          receipt = await tx.wait();
          console.log(
            `TX | signer: ${tx.from}, nonce: ${tx.nonce}, erc20.approve() reset`,
          );
        }

        tx = await this.erc20.approve(this.htlc.address, amount);
        receipt = await tx.wait();
        console.log(
          `TX | signer: ${tx.from}, nonce: ${tx.nonce}, erc20.approve()`,
        );
      }
    } catch (error) {
      console.error(`Error erc20.approve()`);
      throw error;
    }
    return { tx, receipt };
  }

  async newContract(
    chainId: string,
    hashlock: string,
    timelock: number,
    receiver: string,
    amount: BigNumber,
  ): Promise<NewContractType> {
    let tx: ethers.ContractTransaction;
    let receipt: ethers.ContractReceipt;
    let result;

    const contractId = generateContractIdOfErc20Htlc(
      this.signerAddress,
      receiver,
      this.erc20.address,
      amount,
      hashlock,
      timelock,
    );

    try {
      tx = await this.htlc.newContract(
        chainId,
        timelock,
        amount,
        hashlock,
        receiver,
        this.erc20.address,
      );
      result = {
        contractId,
        tx,
      };
      receipt = await tx.wait();
      result.receipt = receipt;
      console.log(
        `TX | signer: ${tx.from}, nonce: ${tx.nonce}, htlc.newContract()`,
      );
    } catch (error: any) {
      if (error.code === Logger.errors.TRANSACTION_REPLACED) {
        if (error.cancelled) {
          console.error(`Error htlc.newContract()`);
          throw error;
        } else {
          result.receipt = error.receipt;
          console.log(
            `TX | signer: ${result.tx.from}, nonce: ${result.tx.nonce}, htlc.newContract() accelerated`,
          );
        }
      } else {
        throw error;
      }
    }
    return result;
  }

  async withdraw(hashlock: string, preimage: string): Promise<TxReturnType> {
    let tx;
    let receipt;
    try {
      tx = await this.htlc.withdraw(hashlock, preimage);
      receipt = await tx.wait();
      console.log(
        `TX | signer: ${tx.from}, nonce: ${tx.nonce}, htlc.withdraw()`,
      );
    } catch (error) {
      console.error(`Error htlc.withdraw()`);
      throw error;
    }
    return { tx, receipt };
  }

  async refund(hashlock: string): Promise<TxReturnType> {
    let tx: ethers.ContractTransaction;
    let receipt: ethers.ContractReceipt;
    try {
      tx = await this.htlc.refund(hashlock);
      receipt = await tx.wait();
      console.log(`TX | signer: ${tx.from}, nonce: ${tx.nonce}, htlc.refund()`);
    } catch (error) {
      console.error(`Error htlc.refund()`);
      throw error;
    }
    return { tx, receipt };
  }
}
